import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Paginated, Standard, StandardVersion } from '@grc/types';
import { apiFetch, ApiRequestError } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';
import {
  Badge,
  Button,
  Card,
  Dialog,
  Field,
  LoadingPane,
  PageHeader,
  Textarea,
  useToast,
} from '../../components/ui.js';

export function StandardsPage() {
  const { me } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const isSystemAdmin = me?.user.isSystemAdmin ?? false;

  const standards = useQuery({
    queryKey: ['standards'],
    queryFn: () => apiFetch<Paginated<Standard>>('/standards', { query: { limit: 100 } }),
  });
  const versionQueries = useQueries({
    queries: (standards.data?.items ?? []).map((s) => ({
      queryKey: ['standard-versions', s.id],
      queryFn: () => apiFetch<StandardVersion[]>(`/standards/${s.id}/versions`),
    })),
  });

  const [importOpen, setImportOpen] = useState(false);
  const [bundleJson, setBundleJson] = useState('');
  const importBundle = useMutation({
    mutationFn: () => {
      const parsed = JSON.parse(bundleJson);
      return apiFetch<{ clauseCount: number; questionCount: number }>('/standards/import', {
        method: 'POST',
        body: parsed,
      });
    },
    onSuccess: (result) => {
      toast(`Imported: ${result.clauseCount} clauses, ${result.questionCount} questions.`, 'success');
      setImportOpen(false);
      setBundleJson('');
      void queryClient.invalidateQueries({ queryKey: ['standards'] });
    },
    onError: (err) =>
      toast(
        err instanceof SyntaxError
          ? 'That is not valid JSON.'
          : err instanceof ApiRequestError
            ? err.message
            : 'Import failed',
        'error',
      ),
  });

  const syncTemplates = useMutation({
    mutationFn: () => apiFetch<{ synced: number }>('/ai/templates/sync', { method: 'POST' }),
    onSuccess: (r) => toast(`Synced ${r.synced} AI prompt templates.`, 'success'),
    onError: (err) =>
      toast(err instanceof ApiRequestError ? err.message : 'Sync failed', 'error'),
  });

  if (standards.isLoading) return <LoadingPane label="Loading standards" />;

  return (
    <div>
      <PageHeader
        title="Standards catalogue"
        subtitle="The clause structures and question sets available for assessments"
        actions={
          isSystemAdmin && (
            <>
              <Button variant="secondary" onClick={() => syncTemplates.mutate()} loading={syncTemplates.isPending}>
                Sync AI templates
              </Button>
              <Button onClick={() => setImportOpen(true)}>Import bundle</Button>
            </>
          )
        }
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
        {(standards.data?.items ?? []).map((s, i) => {
          const versions = versionQueries[i]?.data ?? [];
          return (
            <Card key={s.id}>
              <h3 style={{ marginBottom: 4 }}>{s.name}</h3>
              <p style={{ color: 'var(--color-text-faint)', fontSize: 'var(--text-xs)', marginBottom: 8 }}>
                {s.issuingBody} · <Badge tone={s.status === 'active' ? 'success' : 'neutral'}>{s.status}</Badge>
              </p>
              <p style={{ color: 'var(--color-text-muted)' }}>{s.description}</p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {versions.map((v) => (
                  <Link key={v.id} to={`/standards/versions/${v.id}`}>
                    <Badge tone={v.id === s.currentVersionId ? 'primary' : 'neutral'}>
                      <span
                        className="badge-ellipsis"
                        title={`${v.label}${v.id === s.currentVersionId ? ' · current' : ''}`}
                      >
                        {v.label}
                        {v.id === s.currentVersionId ? ' · current' : ''}
                      </span>
                    </Badge>
                  </Link>
                ))}
                {versions.length === 0 && (
                  <span style={{ color: 'var(--color-text-faint)', fontSize: 'var(--text-xs)' }}>
                    No versions loaded
                  </span>
                )}
              </div>
            </Card>
          );
        })}
      </div>
      {(standards.data?.items ?? []).length === 0 && (
        <p style={{ color: 'var(--color-text-muted)' }}>
          No standards loaded yet{isSystemAdmin ? ' — import a bundle or run the seed script (npm run db:seed)' : ''}.
        </p>
      )}

      <Dialog open={importOpen} onClose={() => setImportOpen(false)} title="Import a standard bundle">
        <p style={{ color: 'var(--color-text-muted)' }}>
          Paste a StandardImportBundle JSON document (see docs/CONTRACTS.md). Re-importing an
          existing version replaces its definition unless assessments already use it.
        </p>
        <Field label="Bundle JSON" required>
          {(props) => (
            <Textarea
              {...props}
              required
              value={bundleJson}
              onChange={(e) => setBundleJson(e.target.value)}
              style={{ minHeight: 220, fontFamily: 'var(--font-mono)', fontSize: 12 }}
            />
          )}
        </Field>
        <div className="dialog-actions">
          <Button variant="secondary" onClick={() => setImportOpen(false)}>
            Cancel
          </Button>
          <Button loading={importBundle.isPending} onClick={() => importBundle.mutate()}>
            Import
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
